import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../components/header/header.component';
import { GlobalService } from '../../services/global.service';
import { ModalComponent } from '../../components/modal/modal.component';
import { PluralPipe } from '../../pipes/plural.pipe';
import { AgGridListComponent } from "../../components/ag-grid-list/ag-grid-list.component";
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { UniversityService } from '../../services/university.service';
import { SearchInputComponent } from '../../components/search-input/search-input.component';
import { ButtonComponent } from '../../components/button/button.component';
import { ToggleTabsComponent } from '../../components/toggle-tabs/toggle-tabs.component';
import { EvaluationService } from '../../services/evaluation.service';
import { ReEvaluationService } from '../../services/re-evaluation.service';
import { DropdownService } from '../../services/dropdown.service';
import { FormsModule } from '@angular/forms';
import { ListActionButtonsComponent } from '../../components/list-action-buttons/list-action-buttons.component';
import { ScriptService } from '../../services/script.service';
import { HttpService } from '../../services/http.service';

interface assignment_pick {
  request_id: number;
  evaluator_id: number | null;
  head_evaluator_id: number | null;
}

@Component({
  selector: 'app-re-evaluations-list-page',
  imports: [
    HeaderComponent, CommonModule, ModalComponent, PluralPipe, AgGridListComponent,
    SelectInputComponent, SearchInputComponent, ButtonComponent, ToggleTabsComponent, FormsModule,
    ListActionButtonsComponent,
  ],
  templateUrl: './re-evaluations-list-page.component.html',
})
export class ReEvaluationsListPageComponent {
  @ViewChild('exam_dropdown') exam_dropdown: SelectInputComponent | undefined;
  page_name = 'Re-evaluations';
  private _suppress_exam_change = false;
  params: any = {};
  selected_ids: any = {};
  selected_request_ids: number[] = [];
  picks_by_request: { [request_id: number]: assignment_pick } = {};
  evaluator_options: any[] = [];
  head_evaluator_options: any[] = [];
  items: any;
  isLoading: boolean = false;
  is_assigning: boolean = false;
  status_counts = { pending: 0, in_progress: 0, completed: 0, all: 0 };

  // Student-only filters
  student_exam_list: any[] = [];
  selected_academic_year_id: any = '';
  selected_exam_id: any = '';

  // Active grid columns — set in ngOnInit based on role
  re_evaluations_cols: any[] = [];

  // Admin queue columns (full breakdown)
  admin_columns: any[] = [
    { headerName: 'Sr.No', field: 'SrNo', flex: 1 },
    { field: 'application_date', headerName: 'Application Date', minWidth: 150, flex: 1 },
    { field: 'academic_year', headerName: 'Academic Year', minWidth: 140, flex: 1 },
    { field: 'university', headerName: 'University', minWidth: 150, flex: 1 },
    { field: 'program', headerName: 'Program', minWidth: 130, flex: 1 },
    { field: 'stream', headerName: 'Stream', minWidth: 130, flex: 1 },
    { field: 'exam_name', headerName: 'Exam Name (Code)', minWidth: 160, flex: 1 },
    { field: 'subject', headerName: 'Subject (Code)', minWidth: 150, flex: 1 },
    { field: 'total_marks', headerName: 'Total Marks', minWidth: 140, flex: 1 },
    { field: 'marks_obtained', headerName: 'Marks Obtained', minWidth: 150, flex: 1 },
    { field: 're_evaluated_marks', headerName: 'Re-Evaluated Marks', minWidth: 170, flex: 1 },
    { field: 'status', headerName: 'Status', minWidth: 120, flex: 1, cellRenderer: this.status_chip_renderer.bind(this) },
  ];

  // Student status columns (Figma image-1)
  student_columns: any[] = [
    { field: 'application_date', headerName: 'Application Date', minWidth: 150, flex: 1 },
    { field: 'subject', headerName: 'Subject (Code)', minWidth: 160, flex: 1 },
    { field: 'script_id', headerName: 'Script ID', minWidth: 140, flex: 1 },
    { field: 'total_marks', headerName: 'Total Marks', minWidth: 130, flex: 1 },
    {
      field: 'marks_obtained',
      headerName: 'Marks Obtained',
      minWidth: 140,
      flex: 1,
      cellRenderer: (p: any) => {
        const m = p.data?.marks_obtained;
        if (m === null || m === undefined || m === '-' || m === '') return '<span style="color:#9ca3af">-</span>';
        return String(m);
      },
    },
    {
      field: 're_evaluated_marks',
      headerName: 'Revised Marks',
      minWidth: 130,
      flex: 1,
      cellRenderer: (p: any) => {
        const m = p.data?.re_evaluated_marks;
        if (m === null || m === undefined || m === '-' || m === '') return '<span style="color:#9ca3af">-</span>';
        return `<span style="font-weight:600">${m}</span>`;
      },
    },
    { field: 'status', headerName: 'Status', minWidth: 120, flex: 1, cellRenderer: this.status_chip_renderer.bind(this) },
  ];

  status_chip_renderer(p: any): string {
    const status = (p.data?.status || '').toString().toLowerCase();
    if (status === 'completed') return '<span style="background:#D1FAE5;color:#065F46;padding:4px 14px;border-radius:20px;font-size:13px;font-weight:600;">Completed</span>';
    if (status === 'in_progress' || status === 'in progress') return '<span style="background:#FEF3C7;color:#92400E;padding:4px 14px;border-radius:20px;font-size:13px;font-weight:600;">In progress</span>';
    if (status === 'pending') return '<span style="background:#E0E7FF;color:#3730A3;padding:4px 14px;border-radius:20px;font-size:13px;font-weight:600;">Pending</span>';
    return p.data?.status || '-';
  }

  is_admin_view: boolean = false;
  is_student_view: boolean = false;

  tabList: any[] = [
    { name: 'All', action: () => this.filter('All') },
    { name: 'Pending', action: () => this.filter('Pending') },
    { name: 'In Progress', action: () => this.filter('In Progress') },
    { name: 'Completed', action: () => this.filter('Completed') },
  ];
  current_status: string = 'All';

  filter(type: string) {
    this.current_status = type;
    this.get_list(this.build_filters({ page: '1', page_size: '50', ...(type !== 'All' ? { status: this.api_status(type) } : {}) }));
  }

  api_status(label: string): string {
    return label === 'In Progress' ? 'in_progress' : label.toLowerCase();
  }

  actions(row: any) {
    return [
      { type: 'icon-filled', icon: 'bx bx-eye', text: 'View', function: this.open_view.bind(this), disabled: !row?.script_db_id },
    ];
  }

  constructor(
    public gs: GlobalService,
    public route: Router,
    public us: UniversityService,
    public ar: ActivatedRoute,
    public es: EvaluationService,
    public res: ReEvaluationService,
    public dds: DropdownService,
    public ss: ScriptService,
    public http: HttpService,
  ) { }

  async ngOnInit() {
    const role_data = this.gs.user_role_data || {};
    this.is_admin_view = !!(role_data.evaluations_and_revaluations_re_evaluation_assign
      || role_data.evaluations_and_revaluations_re_evaluation_view
      || (this.gs.user_data?.role === 'Super Admin'));
    this.is_student_view = !this.is_admin_view && (
      role_data.student_re_evaluation_request === true
      || this.gs.user_data?.role === 'Student'
    );

    if (this.is_admin_view) {
      this.re_evaluations_cols = [...this.admin_columns];
    } else {
      this.re_evaluations_cols = [
        ...this.student_columns,
        { headerName: 'Actions', sortable: false, cellRenderer: ListActionButtonsComponent, headerClass: 'custom-header-center', cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' }, cellRendererParams: { actions: this.actions.bind(this), inline: true }, editable: false, pinned: 'right', width: 100, minWidth: 100, maxWidth: 100 },
      ];
    }

    if (this.is_student_view) {
      await this.dds?.get_dropdown_list(['academic-year']);
      this.build_student_exam_list();
    }
    if (this.is_admin_view) {
      await this.load_evaluator_options();
    }

    this.ar.queryParams.subscribe(async (params: any) => {
      this.params = { ...params };
      const status_param = params.status && params.status !== 'All' ? this.api_status(params.status) : null;
      if (params?.payment) {
        await this.handle_payment_redirect(params);
      }
      this.get_list(this.build_filters({ page: '1', page_size: '50', ...(status_param ? { status: status_param } : {}) }));
    });
  }

  // Handles the redirect issued by /payment/billdesk-callback. Shows the right
  // toast, optionally confirms the status against BE (handles redirect-faster-
  // than-DB-commit), and exposes a retry button when the payment failed.
  pending_failed_request_id: string | null = null;
  is_retrying_payment = false;

  private async handle_payment_redirect(params: any) {
    const status: string = (params?.payment || '').toLowerCase();
    const request_id: string = params?.request_id || '';

    // Confirm against BE so the toast reflects the persisted state, not the
    // (possibly stale) redirect param.
    let confirmed_status = status;
    if (request_id) {
      try {
        const res: any = await this.res.get_payment_status(request_id);
        const ps = (res?.data?.payment_status || '').toLowerCase();
        if (ps === 'success') confirmed_status = 'success';
        else if (ps === 'cancelled') confirmed_status = 'cancelled';
        else if (ps === 'expired') confirmed_status = 'cancelled';
        else if (ps === 'failed') confirmed_status = 'failed';
      } catch { /* fall back to redirect param */ }
    }

    if (confirmed_status === 'success') {
      this.gs.toastr_shows_function('Payment successful. Re-evaluation application submitted successfully', 'Success', 'success');
      this.pending_failed_request_id = null;
    } else if (confirmed_status === 'cancelled') {
      this.gs.toastr_shows_function('Re-evaluation process cancelled.', 'Info', 'info');
      this.pending_failed_request_id = request_id || null;
    } else if (confirmed_status === 'failed' || confirmed_status === 'error') {
      this.gs.toastr_shows_function('Payment unsuccessful. Re-evaluation application not submitted', 'Error', 'error');
      this.pending_failed_request_id = request_id || null;
    }

    // Clear the query params so a page refresh doesn't re-fire the toast
    this.route.navigate([], { relativeTo: this.ar, queryParams: {}, replaceUrl: true });
  }

  async retry_failed_payment() {
    if (!this.pending_failed_request_id || this.is_retrying_payment) return;
    this.is_retrying_payment = true;
    try {
      const res: any = await this.res.retry_payment(this.pending_failed_request_id);
      if (res?.data?.payment_required && res?.data?.billdesk_payload) {
        this.submit_billdesk_form(res.data.billdesk_payload);
        return;
      }
      this.gs.toastr_shows_function(res?.message || 'Retry initiated', 'Success', 'success');
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Retry failed';
      this.gs.toastr_shows_function(message, 'Error', 'error');
    } finally {
      this.is_retrying_payment = false;
    }
  }

  private submit_billdesk_form(payload: any) {
    const data = payload?.links?.[1];
    if (!data?.href || !data?.parameters) {
      this.gs.toastr_shows_function('Invalid payment payload from server', 'Error', 'error');
      return;
    }
    const form = document.createElement('form');
    form.method = (data.method || 'POST').toUpperCase();
    form.action = data.href;
    const params = { ...data.parameters };
    if (params.mercid != null && params.merchantid == null) {
      params.merchantid = params.mercid;
      delete params.mercid;
    }
    Object.keys(params).forEach((key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(params[key] ?? '');
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  }

  build_filters(extra: any = {}): any {
    const f: any = { ...extra, include_counts: 'true' };
    if (this.is_student_view) {
      if (this.selected_academic_year_id) f.academic_year_id = this.selected_academic_year_id;
      if (this.selected_exam_id) f.exam_id = this.selected_exam_id;
    }
    return f;
  }

  build_student_exam_list() {
    const mappings: any[] = this.gs.user_data?.student_mappings || [];
    const ay_id = this.selected_academic_year_id ? Number(this.selected_academic_year_id) : null;
    if (!ay_id) {
      this.student_exam_list = [];
      return;
    }
    const scoped = mappings.filter((m: any) => Number(m?.AcademicYear?.id ?? m?.academic_year_id) === ay_id);
    const seen = new Set<number>();
    this.student_exam_list = scoped
      .filter((m: any) => m?.Exam?.id && !seen.has(m.Exam.id) && seen.add(m.Exam.id))
      .map((m: any) => ({ ...m.Exam, title: m.Exam.name, value: m.Exam.id }));
  }

  on_student_exam_change(event: any) {
    if (this._suppress_exam_change) return;
    this.selected_exam_id = event?.value?.id || event?.value || '';
    this.get_list(this.build_filters({ page: '1', page_size: '50', ...(this.current_status !== 'All' ? { status: this.api_status(this.current_status) } : {}) }));
  }

  on_student_academic_year_change(event: any) {
    this.selected_academic_year_id = event?.value?.id || event?.value || '';
    this.selected_exam_id = '';
    this._suppress_exam_change = true;
    this.exam_dropdown?.reset();
    this._suppress_exam_change = false;
    this.build_student_exam_list();
    if (this.selected_academic_year_id && this.student_exam_list.length) {
      return;
    }
    this.get_list(this.build_filters({ page: '1', page_size: '50', ...(this.current_status !== 'All' ? { status: this.api_status(this.current_status) } : {}) }));
  }

  async load_evaluator_options() {
    try {
      await this.dds?.get_dropdown_list(['user']);
      const all_users: any[] = this.dds.dropdown_list?.user_list || [];
      this.evaluator_options = all_users
        .filter((u: any) => u?.Role?.evaluations_and_revaluations_re_evaluation_update)
        .map((u: any) => ({ title: `${u.name} (${u.email || ''})`, value: u.id }));
      this.head_evaluator_options = all_users
        .filter((u: any) => u?.Role?.evaluations_and_revaluations_re_evaluation_head_view)
        .map((u: any) => ({ title: `${u.name} (${u.email || ''})`, value: u.id }));
    } catch (error) {
      console.warn('Could not preload evaluator options for re-eval queue', error);
    }
  }

  async get_list(filter?: any) {
    if (this.isLoading) return;

    try {
      this.isLoading = true;
      const filters = { ...(filter || this.build_filters({ page: '1', page_size: '50' })) };
      if (!filters.status || filters.status === 'All') {
        delete filters.status;
      } else if (typeof filters.status === 'string') {
        filters.status = this.api_status(filters.status);
      }

      const response = await this.res.get_list(filters);

      if (response?.counts) {
        this.status_counts = response.counts;
        this.tabList = [
          { name: 'All', action: () => this.filter('All') },
          { name: 'Pending', action: () => this.filter('Pending') },
          { name: 'In Progress', action: () => this.filter('In Progress') },
          { name: 'Completed', action: () => this.filter('Completed') },
        ].map((t) => ({
          ...t,
          label: this.tab_label(t.name),
        }));
      }

      if (response?.data) {
        this.picks_by_request = {};
        // Until the grid wrapper exposes a row-selection event we treat the
        // currently visible rows as the working set for bulk Assign / Auto-Assign.
        this.selected_request_ids = response.data.map((item: any) => item.id);
        this.items = {
          ...response,
          data: response.data.map((item: any, index: number) => {
            this.picks_by_request[item.id] = {
              request_id: item.id,
              evaluator_id: item.evaluator_id ?? null,
              head_evaluator_id: item.script?.re_evaluation_assigned_head_evaluator_id ?? null,
            };
            const status_lc = (item.status || '').toString().toLowerCase();
            return {
              id: item.id,
              SrNo: ((parseInt(filters.page) - 1) * parseInt(filters.page_size)) + index + 1,
              application_date: new Date(item.requested_at).toLocaleDateString('en-GB'),
              academic_year: item.script?.Assessment?.AcademicYear?.name || item.script?.Assessment?.exam?.academic_year || '-',
              university: item.script?.Assessment?.university?.name || item.script?.Assessment?.University?.name || '-',
              program: item.script?.Assessment?.program?.name || item.script?.Assessment?.Program?.name || '-',
              stream: item.script?.Assessment?.stream?.name || item.script?.Assessment?.Stream?.name || '-',
              exam_name: `${item.script?.Assessment?.exam?.name || item.script?.Assessment?.Exam?.name || '-'} (${item.script?.Assessment?.exam?.code || item.script?.Assessment?.Exam?.code || '-'})`,
              subject: `${item.script?.Assessment?.subject?.name || item.script?.Assessment?.Subject?.name || '-'} (${item.script?.Assessment?.subject?.code || item.script?.Assessment?.Subject?.code || '-'})`,
              script_id: item.script?.script_id || item.script?.barcode || '-',
              script_db_id: item.script?.id ?? null,
              total_marks: item.script?.Assessment?.total_marks || '-',
              marks_obtained: item.original_evaluation_marks ?? item.script?.result_released_marks ?? '-',
              re_evaluated_marks: (item.status || '').toString().toLowerCase() === 'completed'
                ? (item.total_marks ?? '-')
                : '-',
              status: status_lc === 'in_progress' ? 'In Progress' : (item.status || '').replace(/^./, (c: string) => c.toUpperCase()),
              raw_data: item,
            };
          }),
        };
      }
    } catch (error: any) {
      console.error('Error fetching re-evaluation list:', error);
      this.gs.toastr_shows_function(error?.message || 'Failed to fetch re-evaluation list', 'Error', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  tab_label(name: string): string {
    if (name === 'All') return `All (${this.status_counts.all || 0})`;
    if (name === 'Pending') return `Pending (${this.status_counts.pending || 0})`;
    if (name === 'In Progress') return `In Progress (${this.status_counts.in_progress || 0})`;
    if (name === 'Completed') return `Completed (${this.status_counts.completed || 0})`;
    return name;
  }

  // ---------- Admin actions ----------

  on_evaluator_change(request_id: number, value: any) {
    const pick = this.picks_by_request[request_id] || { request_id, evaluator_id: null, head_evaluator_id: null };
    pick.evaluator_id = value?.value ?? value ?? null;
    this.picks_by_request[request_id] = pick;
  }

  on_head_evaluator_change(request_id: number, value: any) {
    const pick = this.picks_by_request[request_id] || { request_id, evaluator_id: null, head_evaluator_id: null };
    pick.head_evaluator_id = value?.value ?? value ?? null;
    this.picks_by_request[request_id] = pick;
  }

  selected_assignments(): { request_id: number; evaluator_id: number; head_evaluator_id?: number | null }[] {
    return this.selected_request_ids
      .map((id) => this.picks_by_request[id])
      .filter((pick): pick is assignment_pick => !!pick && !!pick.evaluator_id)
      .map((pick) => ({
        request_id: pick.request_id,
        evaluator_id: pick.evaluator_id as number,
        head_evaluator_id: pick.head_evaluator_id ?? null,
      }));
  }

  async assign_selected() {
    const assignments = this.selected_assignments();
    if (assignments.length === 0) {
      this.gs.toastr_shows_function('Select at least one row with an evaluator chosen', 'Info', 'info');
      return;
    }
    if (this.is_assigning) return;
    this.is_assigning = true;
    try {
      const res = await this.res.assign_evaluators(assignments);
      this.gs.toastr_shows_function(res?.message || 'Assignment successful', 'Success', 'success');
      await this.get_list(this.build_filters({ page: '1', page_size: '50', ...(this.current_status !== 'All' ? { status: this.api_status(this.current_status) } : {}) }));
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Assignment failed';
      this.gs.toastr_shows_function(message, 'Error', 'error');
    } finally {
      this.is_assigning = false;
    }
  }

  async auto_assign_selected() {
    if (this.selected_request_ids.length === 0) {
      this.gs.toastr_shows_function('Select at least one row to auto-assign', 'Info', 'info');
      return;
    }
    if (this.is_assigning) return;
    this.is_assigning = true;
    try {
      const res = await this.res.auto_assign(this.selected_request_ids);
      this.gs.toastr_shows_function(res?.message || 'Auto-assigned', 'Success', 'success');
      await this.get_list(this.build_filters({ page: '1', page_size: '50', ...(this.current_status !== 'All' ? { status: this.api_status(this.current_status) } : {}) }));
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Auto-assignment failed';
      this.gs.toastr_shows_function(message, 'Error', 'error');
    } finally {
      this.is_assigning = false;
    }
  }

  // ---------- Student actions ----------

  async open_view(item: any) {
    const script_db_id = item?.script_db_id;
    if (!script_db_id) return;
    try {
      const res: any = await this.ss.get(script_db_id);
      const raw: any[] = res?.data?.ScriptImages || [];
      const images = raw
        .sort((a: any, b: any) => {
          const nameA = a.image_path?.split('/').pop() || '';
          const nameB = b.image_path?.split('/').pop() || '';
          return nameA.localeCompare(nameB, undefined, { numeric: true });
        })
        .map((img: any) => img.image_path);

      if (!images.length) {
        this.gs.toastr_shows_function('No script images found', 'Info', 'info');
        return;
      }

      const images_html = images
        .map((src: string) => `<img src="${this.http.base_url}/media/get-file?file_path=scripts/${src}" style="display:block;width:100%;max-width:900px;margin:0 auto 12px;box-shadow:0 2px 10px rgba(0,0,0,0.35);" />`)
        .join('');

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Script</title><style>* { margin:0;padding:0;box-sizing:border-box; } body { background:#525659;padding:24px 16px;min-height:100vh; }</style></head><body>${images_html}</body></html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const tab = window.open(url, '_blank');
      if (tab) setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Failed to load script images', err);
    }
  }

  async download() {
    try {
      const filters = this.build_filters({ page: '1', page_size: '9999999', ...(this.current_status !== 'All' ? { status: this.api_status(this.current_status) } : {}) });
      const response = await this.res.get_list(filters);
      const rows: any[] = response?.data || [];
      if (!rows.length) {
        this.gs.toastr_shows_function('No data to download', 'Info', 'info');
        return;
      }
      const escape = (v: any) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; };
      const headers = ['Application Date', 'Subject (Code)', 'Script ID', 'Total Marks', 'Marks Obtained', 'Revised Marks', 'Status'];
      let csv = headers.join(',') + '\n';
      for (const item of rows) {
        const status_lc = (item.status || '').toString().toLowerCase();
        csv += [
          escape(new Date(item.requested_at).toLocaleDateString('en-GB')),
          escape(`${item.script?.Assessment?.subject?.name || item.script?.Assessment?.Subject?.name || '-'} (${item.script?.Assessment?.subject?.code || item.script?.Assessment?.Subject?.code || '-'})`),
          escape(item.script?.script_id || item.script?.barcode || '-'),
          escape(item.script?.Assessment?.total_marks || '-'),
          escape(item.original_evaluation_marks ?? item.script?.result_released_marks ?? '-'),
          escape(status_lc === 'completed' ? (item.total_marks ?? '-') : '-'),
          escape(status_lc === 'in_progress' ? 'In Progress' : (item.status || '').replace(/^./, (c: string) => c.toUpperCase())),
        ].join(',') + '\n';
      }
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Re_Evaluations.csv';
      link.click();
      window.URL.revokeObjectURL(url);
      this.gs.toastr_shows_function('Downloaded successfully.', 'Success', 'success');
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.message || 'Download failed', 'Error', 'error');
    }
  }
}
